import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations } from '../i18n/translations';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('de');
  const [isRTL, setIsRTL] = useState(false);

  // langue par défaut si on veut un fallback global
  const FALLBACK_LANGUAGE = 'en';

  useEffect(() => {
    try {
      const savedLanguage = localStorage.getItem('globridge-language');
      if (savedLanguage && translations[savedLanguage]) {
        setLanguage(savedLanguage);
      }
    } catch (e) {
      console.warn('[i18n] Cannot access localStorage, using default language "de".');
    }
  }, []);

  useEffect(() => {
    const rtl = language === 'ar';
    setIsRTL(rtl);

    try {
      localStorage.setItem('globridge-language', language);
    } catch (e) {
      console.warn('[i18n] Cannot write language to localStorage.');
    }

    if (typeof document !== 'undefined') {
      document.documentElement.dir = rtl ? 'rtl' : 'ltr';
      document.documentElement.lang = language;

      const titles = {
        de: 'GLOBRIDGE - Elite Boutique Consulting',
        en: 'GLOBRIDGE - Elite Boutique Consulting',
        ar: 'GLOBRIDGE - استشارات النخبة البوتيك',
        fr: 'GLOBRIDGE - Conseil Boutique Elite'
      };
      document.title = titles[language] || titles.de;
    }
  }, [language]);

  const changeLanguage = (newLanguage) => {
    if (translations[newLanguage]) {
      setLanguage(newLanguage);
    } else {
      console.warn(`[i18n] Unsupported language: ${newLanguage}`);
    }
  };

  const resolveKey = (table, key) => {
    if (!table) return undefined;
    const parts = key.split('.');
    let value = table;
    for (const part of parts) {
      if (value && typeof value === 'object' && part in value) {
        value = value[part];
      } else {
        return undefined;
      }
    }
    return value;
  };

  const t = (key) => {
    // 1) essai dans la langue actuelle
    const current = resolveKey(translations[language], key);
    if (current !== undefined) {
      return current;
    }

    console.warn(
      `[i18n] Missing translation for key "${key}" in language "${language}", trying fallback "${FALLBACK_LANGUAGE}".`
    );

    // 2) fallback anglais (ou langue définie)
    const fallback = resolveKey(translations[FALLBACK_LANGUAGE], key);
    if (fallback !== undefined) {
      return fallback;
    }

    console.warn(
      `[i18n] Missing translation for key "${key}" in fallback language "${FALLBACK_LANGUAGE}". Returning key.`
    );
    return key;
  };

  const getCurrentLanguageInfo = () => {
    const languageInfo = {
      de: { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
      en: { code: 'en', name: 'English', flag: '🇺🇸' },
      ar: { code: 'ar', name: 'العربية', flag: '🇦🇪' },
      fr: { code: 'fr', name: 'Français', flag: '🇫🇷' }
    };
    return languageInfo[language] || languageInfo.de;
  };

  const getAllLanguages = () => {
    return [
      { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
      { code: 'en', name: 'English', flag: '🇺🇸' },
      { code: 'ar', name: 'العربية', flag: '🇦🇪' },
      { code: 'fr', name: 'Français', flag: '🇫🇷' }
    ];
  };

  const value = {
    language,
    changeLanguage,
    t,
    isRTL,
    getCurrentLanguageInfo,
    getAllLanguages
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
